import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { UserAvatar } from '@/components/UserAvatar'
import {
  changedBy,
  fieldLabel,
  formatChangeTime,
  isActionEvent,
} from '@/features/sales-order/changeDescription'
import type { CustomerOrderChange } from '@/types/salesOrder'

/**
 * Everything that has been done to this order, under the document it came from.
 *
 * Folded away by default and counted in its own heading: it answers a question that is asked
 * occasionally and in full — who changed this, and to what — rather than one that should be in
 * the way of validating the order.
 *
 * Field edits are shown once each. The log keeps every save, so a value corrected three times
 * would otherwise read as three entries saying the same thing; the newest is what the order says
 * now, and that is what the list is for. Lifecycle events are all kept: each one happened.
 */
export function CustomerOrderAuditLog({ changes }: { changes: CustomerOrderChange[] }) {
  const [expanded, setExpanded] = useState(false)

  const entries = latestPerField(changes)
  if (entries.length === 0) return null

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className="flex w-full items-center justify-between py-1 text-left"
      >
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Audit log{' '}
          <span className="font-normal normal-case tracking-normal text-gray-300">
            ({entries.length})
          </span>
        </p>
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
        )}
      </button>

      {expanded && (
        <ol className="mt-1.5 space-y-2">
          {entries.map((change, index) => (
            <li key={index} className="flex items-start gap-2 text-xs">
              {change.by ? (
                <UserAvatar firstName={change.by.first_name} lastName={change.by.last_name} />
              ) : (
                <span className="h-6 w-6 shrink-0 rounded-full bg-gray-100" aria-hidden />
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-gray-700">
                  <span className="font-medium">{fieldLabel(change.field_name)}</span>
                  {change.position && change.position !== 'HEAD' && (
                    <span className="text-gray-500"> · row {change.position}</span>
                  )}
                  {/*
                    A lifecycle event is usually the whole of what it says — "Validated" needs no
                    value after it. An added row is the exception: the article it added is what the
                    entry is about, so it is shown like any other value.
                  */}
                  {change.field_name === 'action:row_removed' ? (
                    <>
                      <span className="mx-1 text-gray-400">→</span>
                      <span className="text-gray-900 line-through">{change.old_value}</span>
                    </>
                  ) : (!isActionEvent(change) || change.field_name === 'action:row_added') && (
                    <>
                      <span className="mx-1 text-gray-400">→</span>
                      <span className="text-gray-900">{change.new_value || 'cleared'}</span>
                    </>
                  )}
                </span>
                <span className="block text-gray-400">
                  {changedBy(change)}, {formatChangeTime(change.timestamp)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

/**
 * Every lifecycle event, and the newest edit of each field. The entries arrive newest first, so
 * the first one seen for a field is the one to keep.
 */
function latestPerField(changes: CustomerOrderChange[]): CustomerOrderChange[] {
  const seen = new Set<string>()
  const entries: CustomerOrderChange[] = []

  for (const change of changes) {
    if (isActionEvent(change)) {
      entries.push(change)
      continue
    }

    const key = `${change.position}:${change.field_name}`
    if (seen.has(key)) continue
    seen.add(key)
    entries.push(change)
  }

  return entries
}
